import type { Box } from '@/core/geometry/box';
import { estimateTextWidth, inkOn, isBlankText, isDarkColor, layoutText, lineStart, textAspect, textFont, type MeasureText, type TextBlock, type TextStyle } from '@/core/project/text-layer';
import { graphemes, STILL_FRAME, wordsOf, type TextMotionFrame, type TextReveal } from '@/core/project/text-motion';
import { createCanvas, get2d, type Context2D } from './canvas';

/** Measures with `ctx` in the given font. */
const measureWith =
  (ctx: Context2D): MeasureText =>
  (line, font) => {
    ctx.font = font;
    return ctx.measureText(line).width;
  };

let shared: Context2D | null | undefined;

/** A context to measure with on this thread, or null where canvases are unavailable (tests). */
function measuringContext(): Context2D | null {
  if (shared !== undefined) return shared;
  try {
    shared = get2d(createCanvas(8, 8));
  } catch {
    shared = null;
  }
  return shared;
}

/** Measures text on this thread's canvas, estimating where there is none. */
export const measureText: MeasureText = (line, font) => {
  const ctx = measuringContext();
  return ctx ? measureWith(ctx)(line, font) : estimateTextWidth(line, font);
};

const aspects = new Map<string, number>();
const MAX_CACHED = 200;

/** Width / height of a text layer's block (cached: it is asked for on every render). */
export function measureTextAspect(layer: { readonly text: string; readonly style: TextStyle }): number {
  const key = `${layer.style.look}|${layer.style.font}|${layer.style.bold}|${layer.text}`;
  const cached = aspects.get(key);
  if (cached !== undefined) return cached;
  const aspect = textAspect(layoutText(layer.text, layer.style, measureText));
  if (aspects.size >= MAX_CACHED) aspects.delete(aspects.keys().next().value!);
  aspects.set(key, aspect);
  return aspect;
}

/** A run of letters drawn at `x` (reference pixels) on one line, fully or partly faded in. */
interface Piece {
  readonly text: string;
  readonly x: number;
  readonly alpha: number;
}

/** What each line shows: everything, or as much as the reveal has got to, and how far right it reaches. */
interface VisibleLine {
  readonly pieces: readonly Piece[];
  /** Start and end (reference pixels) of what shows, for the box behind it. */
  readonly from: number;
  readonly to: number;
}

function visibleLines(block: TextBlock, align: TextStyle['align'], reveal: TextReveal | null, measure: (text: string) => number): VisibleLine[] {
  let budget = reveal?.amount ?? Infinity;
  return block.lines.map((line) => {
    const start = lineStart(block, line.width, align);
    if (!reveal || !line.text) return { pieces: line.text ? [{ text: line.text, x: start, alpha: 1 }] : [], from: start, to: start + line.width };
    if (reveal.unit === 'letters') {
      const letters = graphemes(line.text);
      const shown = letters.slice(0, Math.max(0, Math.min(letters.length, Math.floor(budget)))).join('');
      budget -= letters.length;
      return { pieces: shown ? [{ text: shown, x: start, alpha: 1 }] : [], from: start, to: start + (shown ? measure(shown) : 0) };
    }
    const pieces: Piece[] = [];
    let to = start;
    for (const word of wordsOf(line.text)) {
      const alpha = Math.min(1, Math.max(0, budget));
      budget -= 1;
      if (alpha <= 0) break;
      const x = start + measure(line.text.slice(0, word.index));
      pieces.push({ text: word.text, x, alpha });
      to = x + measure(word.text);
    }
    return { pieces, from: start, to };
  });
}

/**
 * Draws a text layer filling `rect` (output pixels): the block is laid out at
 * the reference size and scaled to the rect's width, so preview and export
 * agree however their fonts measure. `motion` moves, scales or reveals it
 * without changing its layout.
 */
export function renderText(ctx: Context2D, layer: { readonly text: string; readonly style: TextStyle }, rect: Box, opacity: number, motion: TextMotionFrame = STILL_FRAME): void {
  const alpha = Math.min(1, Math.max(0, opacity * motion.opacity));
  if (isBlankText(layer.text) || rect.w <= 0 || rect.h <= 0 || alpha <= 0.001 || motion.scale <= 0) return;
  const { style } = layer;
  const block = layoutText(layer.text, style, measureWith(ctx));
  const scale = (rect.w / block.width) * motion.scale;
  ctx.save();
  ctx.globalAlpha = alpha;
  // Scaled around its centre, shifted by a share of its own height.
  ctx.translate(rect.x + rect.w / 2, rect.y + rect.h / 2 + motion.offsetY * rect.h);
  ctx.scale(scale, scale);
  ctx.translate(-block.width / 2, -block.height / 2);
  ctx.font = textFont(style, block.fontSize);
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.lineJoin = 'round';
  const lines = visibleLines(block, style.align, motion.reveal, (text) => ctx.measureText(text).width);
  if (style.look === 'tag') drawPlate(ctx, block, style.color, block.fontSize * 0.28);
  else if (style.look === 'box') drawLineBoxes(ctx, block, lines);
  drawLines(ctx, block, style, lines, scale, alpha);
  ctx.restore();
}

/** One rounded plate behind the whole block, in the text colour. */
function drawPlate(ctx: Context2D, block: TextBlock, color: string, radius: number): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.roundRect(0, 0, block.width, block.height, radius);
  ctx.fill();
}

/** A dark band behind each line, hugging what shows of it (the way subtitles are boxed). */
function drawLineBoxes(ctx: Context2D, block: TextBlock, lines: readonly VisibleLine[]): void {
  ctx.fillStyle = 'rgba(0,0,0,0.66)';
  const padX = block.padX * 0.6;
  lines.forEach((line, row) => {
    if (line.pieces.length === 0) return;
    const y = block.padY + row * block.lineHeight;
    ctx.beginPath();
    ctx.roundRect(line.from - padX, y - block.padY * 0.35, line.to - line.from + padX * 2, block.lineHeight + block.padY * 0.7, block.fontSize * 0.18);
    ctx.fill();
  });
}

function drawLines(ctx: Context2D, block: TextBlock, style: TextStyle, lines: readonly VisibleLine[], scale: number, alpha: number): void {
  const fill = style.look === 'tag' ? inkOn(style.color) : style.color;
  lines.forEach((line, row) => {
    const y = block.padY + row * block.lineHeight + block.lineHeight / 2;
    for (const piece of line.pieces) {
      ctx.globalAlpha = alpha * piece.alpha;
      if (style.look === 'plain') {
        ctx.shadowColor = isDarkColor(style.color) ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.55)';
        // Shadows ignore the canvas transform: sized in output pixels, so preview and export match.
        ctx.shadowBlur = block.fontSize * 0.22 * scale;
        ctx.shadowOffsetY = block.fontSize * 0.03 * scale;
      }
      if (style.look === 'outline') {
        ctx.lineWidth = block.fontSize * 0.14;
        ctx.strokeStyle = isDarkColor(style.color) ? '#ffffff' : '#000000';
        ctx.strokeText(piece.text, piece.x, y);
      }
      ctx.fillStyle = fill;
      ctx.fillText(piece.text, piece.x, y);
      ctx.shadowColor = 'transparent';
    }
  });
}
